// Weekly IVR call synopsis -- data layer. See
// supabase/functions/generate-ivr-weekly-synopsis/index.ts for how a row
// gets generated (pg_cron every Sunday 11:59pm NZT, or the "Generate Now"
// button in src/components/settings/IvrWeeklySynopsisTab.tsx).
import { supabase } from '@/lib/supabase';

export type SynopsisStatus = 'running' | 'completed' | 'failed';
export type SuggestionCategory = 'train' | 'engineer';
export type SuggestionStatus = 'open' | 'in_progress' | 'done' | 'dismissed';

export interface SynopsisSection {
  heading: string;
  body_html: string;
}

export interface TransferReasonTheme {
  label: string;
  count: number;
}

export interface IvrWeeklySynopsis {
  id: string;
  tenant_id: string;
  week_start: string;
  week_end: string;
  generated_at: string;
  status: SynopsisStatus;
  error_message: string | null;
  calls_total: number;
  calls_read: number;
  transferred_count: number;
  zero_attempt_count: number;
  completed_count: number;
  address_trouble_pickup_count: number | null;
  address_trouble_destination_count: number | null;
  sections: SynopsisSection[];
  transfer_reasons: TransferReasonTheme[];
}

export interface IvrSynopsisSuggestion {
  id: string;
  synopsis_id: string;
  category: SuggestionCategory;
  suggestion: string;
  sort_order: number;
  admin_note: string | null;
  status: SuggestionStatus;
  updated_at: string;
}

const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';

export async function loadWeeklySynopses(tenantId?: string | null): Promise<IvrWeeklySynopsis[]> {
  let q = supabase.from('ivr_weekly_synopses').select('*').order('week_start', { ascending: false });
  if (tenantId) q = q.eq('tenant_id', tenantId);
  const { data, error } = await q;
  if (error) {
    console.error('Error loading IVR weekly synopses:', error);
    return [];
  }
  return (data as IvrWeeklySynopsis[]) || [];
}

/** Looks up the synopsis (if any) for one specific Monday-starting week. */
export async function loadSynopsisForWeek(
  tenantId: string | null | undefined,
  weekStart: string,
): Promise<IvrWeeklySynopsis | null> {
  let q = supabase.from('ivr_weekly_synopses').select('*').eq('week_start', weekStart);
  if (tenantId) q = q.eq('tenant_id', tenantId);
  const { data, error } = await q.maybeSingle();
  if (error) {
    console.error('Error loading IVR weekly synopsis for week:', error);
    return null;
  }
  return (data as IvrWeeklySynopsis) || null;
}

export async function loadSynopsisSuggestions(synopsisId: string): Promise<IvrSynopsisSuggestion[]> {
  const { data, error } = await supabase
    .from('ivr_synopsis_suggestions')
    .select('*')
    .eq('synopsis_id', synopsisId)
    .order('sort_order', { ascending: true });
  if (error) {
    console.error('Error loading synopsis suggestions:', error);
    return [];
  }
  return (data as IvrSynopsisSuggestion[]) || [];
}

export async function updateSuggestion(
  id: string,
  patch: { admin_note?: string | null; status?: SuggestionStatus },
): Promise<void> {
  const { error } = await supabase
    .from('ivr_synopsis_suggestions')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Triggers generate-ivr-weekly-synopsis -- the same function pg_cron calls
 *  every Sunday 11:59pm for the current week. Pass weekStart (yyyy-MM-dd,
 *  a Monday) to generate a specific past week instead. */
export async function generateSynopsisNow(tenantId?: string | null, weekStart?: string): Promise<void> {
  const { error } = await supabase.functions.invoke('generate-ivr-weekly-synopsis', {
    body: { tenant_id: tenantId || DEFAULT_TENANT_ID, ...(weekStart ? { week_start: weekStart } : {}) },
  });
  if (error) throw error;
}
